/**
 * Razorpay pay step — one "Pay Now" button that opens Razorpay's own Standard
 * Checkout with every method enabled (UPI, cards, netbanking, wallets). We used
 * to pre-filter into per-method tiles (UPI app / Cards / Netbanking) before
 * opening Razorpay, but Razorpay always shows its own method picker inside the
 * checkout window regardless — so the tiles just added a redundant, identical
 * "choose a method" screen before Razorpay's own. Letting Razorpay's widget
 * handle method selection end-to-end removes that duplicate step.
 */
import React, { useEffect, useRef, useState } from 'react';
import StepHeader from './StepHeader';
import { ErrorBanner } from '../../lib/ui/Feedback';
import Button from '../../lib/ui/Button';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const PUBLIC_KEY = process.env.RAZORPAY_KEY_ID || '';

function loadCheckoutScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const existing = document.querySelector('script[data-nrk-razorpay]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay SDK')));
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.dataset.nrkRazorpay = '1';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
    document.head.appendChild(s);
  });
}

// A checkout session is only ever a Razorpay order id + key — no NRK order
// exists until payment is verified. If the customer sits on this screen too
// long, we transparently issue a fresh session rather than let them pay
// against a stale one.
const SESSION_MAX_AGE_MS = 15 * 60 * 1000;

export default function RazorpayPayStep({
  orderResult, te, L, loading, error,
  onPaid, onBack, onRetrySession, onSupportOpen,
}) {
  const [status, setStatus] = useState('ready');
  const [localError, setLocalError] = useState('');
  const pollRef = useRef(null);
  const amount = Number(orderResult?.amount_due ?? orderResult?.amount ?? orderResult?.total_amount ?? 0);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const verifyOnce = async () => {
    const id = orderResult?.order_id || orderResult?.order_number;
    const rzpId = orderResult?.razorpay_order_id;
    if ((!id && !rzpId) || !API) return false;
    try {
      const headers = orderResult?.authToken ? { Authorization: `Bearer ${orderResult.authToken}` } : {};
      const qs = id ? `order_id=${encodeURIComponent(id)}` : `rzp_order_id=${encodeURIComponent(rzpId)}`;
      const res = await fetch(`${API}/api/payments/razorpay/verify?${qs}`, { credentials: 'include', headers });
      const data = await res.json();
      if (data.paid) {
        if (pollRef.current) clearInterval(pollRef.current);
        onPaid({ ...orderResult, order_id: data.order_id, order_number: data.order_number, payment_status: 'verified' });
        return true;
      }
    } catch { /* ignore */ }
    return false;
  };

  const startPolling = async () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setStatus('checking');
    setLocalError('');
    const paid = await verifyOnce();
    if (paid) return;
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      const ok = await verifyOnce();
      if (ok || tries >= 24) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (!ok) {
          setStatus('ready');
          setLocalError(L('Payment not received yet. Try again, or use Cash on Delivery.', 'చెల్లింపు రాలేదు. మళ్ళీ ప్రయత్నించండి, లేదా COD వాడండి.'));
        }
      }
    }, 5000);
  };

  const verifyWithSignature = async (response) => {
    const headers = { 'Content-Type': 'application/json', ...(orderResult?.authToken ? { Authorization: `Bearer ${orderResult.authToken}` } : {}) };
    const res = await fetch(`${API}/api/payments/razorpay/verify`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        order_id: orderResult?.order_id,
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Payment verification failed');
    if (data.paid) {
      onPaid({ ...orderResult, order_id: data.order_id, order_number: data.order_number, payment_status: 'verified' });
      return true;
    }
    return false;
  };

  const openCheckout = async () => {
    setLocalError('');
    setStatus('opening');
    try {
      let keyId = orderResult?.key_id || PUBLIC_KEY;
      let rzpOrderId = orderResult?.razorpay_order_id;
      let amountPaise = orderResult?.amount_paise || Math.round(amount * 100);
      let prefill = orderResult?.prefill || {};
      const stale = !rzpOrderId
        || !orderResult?.createdAt
        || (Date.now() - orderResult.createdAt) > SESSION_MAX_AGE_MS;

      if (stale && onRetrySession) {
        const refreshed = await onRetrySession();
        if (!refreshed) { setStatus('ready'); return; }
        keyId = refreshed.key_id || keyId;
        rzpOrderId = refreshed.razorpay_order_id;
        amountPaise = refreshed.amount_paise || amountPaise;
        prefill = refreshed.prefill || prefill;
      }

      if (!keyId || !rzpOrderId) throw new Error(L('Payment session missing. Try again.', 'చెల్లింపు సెషన్ లేదు. మళ్ళీ ప్రయత్నించండి.'));
      if (!(amountPaise >= 100)) throw new Error(L('Amount too small for online payment', 'ఆన్‌లైన్ చెల్లింపుకు మొత్తం చాలా తక్కువ'));

      await loadCheckoutScript();

      const nrkRef = orderResult?.order_number || orderResult?.order_id || '';
      const callbackUrl = API
        ? (nrkRef
          ? `${API}/api/payments/razorpay/return?nrk_order=${encodeURIComponent(nrkRef)}`
          : `${API}/api/payments/razorpay/return?rzp_order_id=${encodeURIComponent(rzpOrderId)}`)
        : (nrkRef
          ? `${window.location.origin}/?rzp_return=1&order_id=${encodeURIComponent(nrkRef)}`
          : `${window.location.origin}/?rzp_return=1&rzp_order_id=${encodeURIComponent(rzpOrderId)}`);

      const options = {
        key: keyId,
        amount: amountPaise,
        currency: 'INR',
        name: 'NRK Mart',
        description: nrkRef ? `Order ${nrkRef}` : 'NRK Mart order',
        order_id: rzpOrderId,
        prefill,
        theme: { color: '#1a3d16' },
        callback_url: callbackUrl,
        redirect: false,
        // No method/config.display override — Razorpay shows its full native
        // picker (UPI, cards, netbanking, wallets) in one place.
        handler: async (response) => {
          try {
            setStatus('checking');
            const ok = await verifyWithSignature(response);
            if (!ok) {
              setLocalError(L('Confirming your payment…', 'చెల్లింపు నిర్ధారిస్తున్నాం…'));
              await startPolling();
            }
          } catch (e) {
            setLocalError(e.message || 'Verification failed');
            setStatus('ready');
          }
        },
        modal: { ondismiss: () => { setStatus('ready'); verifyOnce(); } },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        const msg = resp?.error?.description || resp?.error?.reason || 'Payment failed';
        setLocalError(msg);
        setStatus('ready');
      });
      rzp.open();
      setStatus('ready');
      startPolling();
    } catch (e) {
      setLocalError(e.message || 'Could not open payment');
      setStatus('ready');
    }
  };

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') verifyOnce(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line
  }, [orderResult?.order_id]);

  const handleClose = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    onBack();
  };

  const busy = loading || status === 'opening' || status === 'checking';

  return (
    <div>
      <StepHeader title={`💳 ${L('Pay Online', 'ఆన్‌లైన్ చెల్లింపు')}`} onClose={handleClose} />

      <div className="bg-gradient-to-br from-brand-light to-gold-light/60 rounded-2xl px-4 py-4 mb-5 text-center">
        {orderResult?.order_number ? (
          <div className="font-bold text-brand-dark text-[13px]">{L('Order', 'ఆర్డర్')} #{orderResult.order_number}</div>
        ) : null}
        <div className="mt-1 font-black text-3xl text-brand-dark">₹{amount.toFixed(2)}</div>
        <div className="text-xs text-ink-soft mt-1">{L('UPI · Cards · Netbanking · Wallets', 'UPI · కార్డులు · నెట్‌బ్యాంకింగ్ · వాలెట్లు')}</div>
      </div>

      <Button variant="accent" size="lg" fullWidth loading={busy} onClick={openCheckout}>
        {status === 'opening'
          ? L('Opening…', 'తెరుస్తున్నాం…')
          : status === 'checking'
            ? L('Confirming payment…', 'చెల్లింపు నిర్ధారిస్తున్నాం…')
            : `💳 ${L('Pay Now', 'ఇప్పుడు చెల్లించండి')} ₹${amount.toFixed(2)}`}
      </Button>
      <div className="text-center text-xs text-ink-faint mt-2.5 leading-relaxed">
        {L("You'll choose PhonePe, GPay, a card, or netbanking in Razorpay's secure window.", "Razorpay సురక్షిత విండోలో PhonePe, GPay, కార్డు లేదా నెట్‌బ్యాంకింగ్ ఎంచుకోండి.")}
      </div>

      <ErrorBanner>{error || localError}</ErrorBanner>

      <Button variant="ghost" fullWidth className="mt-3" onClick={handleClose}>← {L('Cancel payment', 'చెల్లింపు రద్దు')}</Button>

      <div className="flex items-center justify-center gap-3.5 mt-3.5 pt-3 border-t border-black/5">
        <span className="text-ink-faint text-xs">{L('Need help?', 'సహాయం కావాలా?')}</span>
        <button type="button" onClick={onSupportOpen} className="bg-gold-light text-gold rounded-full px-4 py-1.5 font-extrabold text-xs">🆘 {L('Contact Support', 'సపోర్ట్ సంప్రదించు')}</button>
      </div>
    </div>
  );
}
